/**
 * Oppsett og oppfølging (#267, Settings V1.1 S2): what the company's saved data says is missing, as
 * tasks with an exact destination. One rule per task, read from the same facts the pages themselves
 * use, so the follow-up and the destination never disagree. A source that has not loaded gives no
 * task (never «all done»); one that failed is named as unknown. A task the reader cannot fix is not
 * shown. «Senere» hides an optional task for this person; it never marks anything as done.
 */
import { T } from '@/i18n';
import { fullName, monthLabel } from '@/ui/format';

export type Load<X> = { state: 'pending' } | { state: 'failed' } | { state: 'ready'; data: X };

export interface Person {
  id: number;
  name: string;
  lastname?: string | null;
  employee_id?: string | null | undefined;
  active_status?: boolean;
}

export interface FollowUpInput {
  /** the company's people; null when the reader may not change people (the task is not theirs) */
  people: Load<Person[]> | null;
  /** the company's ordinary pay code; null when the reader is not the administrator */
  regularPayCode: Load<string | null> | null;
  /** the payroll system's direct link; null when the company's system has none or the reader may not connect */
  link: Load<{ available: boolean; connected: boolean; system: string }> | null;
  /** sales days typed in for last month; null when the reader may not see cost or the plan is off */
  sales: Load<number> | null;
  /** last month, YYYY-MM */
  lastMonth: string;
}

export interface Task {
  id: string;
  kind: 'required' | 'optional';
  /** why it matters, before the title: «Før lønnsfilen lages», «Valgfritt, Rapporter» */
  eyebrow: string;
  title: string;
  line: string;
  to: string;
  action: string;
}

export interface FollowUps {
  required: Task[];
  optional: Task[];
  /** sources that failed: the follow-up says it is incomplete, never that nothing is missing */
  unknown: string[];
}

export function followUps(x: FollowUpInput): FollowUps {
  const required: Task[] = [];
  const optional: Task[] = [];
  const unknown: string[] = [];

  if (x.people?.state === 'failed') unknown.push(T('ansatte'));
  if (x.people?.state === 'ready') {
    // a reader who is not sent the number is not told it is missing (undefined is unknown, not empty)
    const active = x.people.data.filter((p) => p.active_status !== false && p.employee_id !== undefined);
    const missing = active.filter((p) => !p.employee_id?.trim());
    const one = missing.length === 1 ? missing[0] : undefined;
    if (missing.length)
      required.push({
        id: 'ansattnummer',
        kind: 'required',
        eyebrow: T('Før lønnsfilen lages'),
        title: one
          ? T('Én ansatt mangler ansattnummer')
          : T('{n} ansatte mangler ansattnummer', { n: missing.length }),
        line: `${T('{a} av {b} aktive ansatte har ansattnummer.', {
          a: active.length - missing.length,
          b: active.length,
        })} ${missing
          .slice(0, 3)
          .map((p) => fullName(p))
          .join(', ')}${missing.length > 3 ? ` ${T('og {n} til', { n: missing.length - 3 })}` : ''}.`,
        // one person opens that person; several open exactly the people missing a number (SET-04)
        to: one ? `/ansatte/${one.id}` : '/ansatte?oppgave=ansattnummer',
        action: one ? T('Fullfør ansattnummer') : T('Se ansatte uten nummer'),
      });
  }

  if (x.regularPayCode?.state === 'ready' && !x.regularPayCode.data?.trim())
    required.push({
      id: 'lonnsart',
      kind: 'required',
      eyebrow: T('Før lønnsfilen lages'),
      title: T('Lønnsart for vanlige timer mangler'),
      line: T('Uten den står de vanlige timene uten lønnsart i lønnsfilen.'),
      to: '/innstillinger/lonn#lonnsart',
      action: T('Legg inn lønnsart'),
    });

  if (x.link?.state === 'failed') unknown.push(T('tilkoblingen til lønnssystemet'));
  if (x.link?.state === 'ready' && x.link.data.available && !x.link.data.connected)
    optional.push({
      id: 'kobling',
      kind: 'optional',
      eyebrow: T('Valgfritt, Lønn'),
      title: T('Koble til {system}', { system: x.link.data.system }),
      line: T('Da går godkjente timer rett til lønn. Lønnsfilen virker uten tilkobling.'),
      to: '/innstillinger/lonn#integrasjon',
      action: T('Se tilkoblingen'),
    });

  if (x.sales?.state === 'failed') unknown.push(T('omsetningen'));
  if (x.sales?.state === 'ready' && x.sales.data === 0) {
    const month = monthLabel(x.lastMonth).split(' ')[0]?.toLowerCase() ?? x.lastMonth;
    optional.push({
      id: `omsetning-${x.lastMonth}`,
      kind: 'optional',
      eyebrow: T('Valgfritt, Rapporter'),
      title: T('Se lønnskostnad opp mot omsetning'),
      line: T('Legg inn omsetningen for {month}. Lønnskostnaden for samme periode regnes ut fra vaktene.', {
        month,
      }),
      to: `/omsetning?periode=maned&mnd=${x.lastMonth}`,
      action: T('Legg til omsetning'),
    });
  }

  return { required, optional, unknown };
}

/** The month before `today` (YYYY-MM-DD), as YYYY-MM. */
export function monthBefore(today: string): string {
  const [y, m] = today.split('-').map(Number);
  const d = new Date(Date.UTC(y ?? 2026, (m ?? 1) - 2, 1));
  return d.toISOString().slice(0, 7);
}

/** What this person put away: the whole block collapsed, and optional tasks for later (#267 S2). */
export interface Dismissed {
  hidden: boolean;
  later: string[];
}
